/**
 * Verifisert oversikt over hvilke rapporttyper som faktisk kan berikes med data
 * ved utskrift, og hva de kan berikes med.
 *
 * KILDE (verifisert, ikke avledet fra kommentarer):
 *  - BizClick.WMS.Core/Services/PrintingService.cs  — hvilken LabelContext hver Print*-metode bygger
 *  - BizClick.WMS.Data/Repositories/LabelDataResolver.cs — hvilke tabeller konteksten slår opp
 *  - BizClick.WMS.Core/Services/Cargonizer/TaProviders/TaLabelPrinter.cs — TA-fraktetikett (logges som 99)
 *
 * VIKTIG: Dette er en frontend "advisory"-konstant. Den må holdes i synk manuelt
 * hvis PrintingService endrer hvilken kontekst en rapporttype bygger. Se HTML-dok
 * `public/rapporttyper-labelcontext-oversikt.html` for full begrunnelse.
 */

export type EnrichmentKind =
  | 'db'            // Beriket fra database via LabelContext
  | 'override-only' // Tom kontekst — kun verdier avsender sender som override + firma/system
  | 'document'      // Dokumentrapport — LayoutJson brukes ikke ved utskrift i dag
  | 'legacy';       // Definert, men ingen aktiv utskriftsflyt produserer typen

export interface EnrichmentInfo {
  kind: EnrichmentKind;
  /** Tabeller som fylles automatisk fra DB for denne typen (utover firma/system). */
  dbSources: string[];
  /** Kort forklaring (norsk). */
  note: string;
}

/**
 * Firma-/systemverdier fylles ALLTID (SystemSetup slås opp betingelsesløst,
 * pluss statiske verdier som dato/klokke/operatør). Gjelder alle typer.
 */
export const ALWAYS_AVAILABLE_LABEL = 'Firma/system (alltid)';

/** System-plassholdere som alltid fylles automatisk, uansett rapporttype. */
const SYSTEM_KEYS = new Set<string>([
  'CompanyName', 'CompanyAddress', 'CompanyOrgNo',
  'CurrentDate', 'CurrentTime', 'OperatorName',
]);

/**
 * Enkle plassholder-nøkler → kilde-tabell (speiler SystemAliases i LabelDataResolver).
 * Brukes til å avgjøre om en plassholder fylles fra DB for en gitt type.
 */
const KEY_SOURCE_TABLE: Record<string, string> = {
  // Vare
  ItemNo: 'Items', ItemName: 'Items', ItemDescription: 'Items', ItemUnitCode: 'Items',
  // Lager
  BinCode: 'StcBin', LocationName: 'StcBin', ZoneName: 'StcZone', WarehouseName: 'Warehouse',
  // Ordre (salg)
  OrderNo: 'Orders', CustomerNo: 'Orders', CustomerName: 'Orders',
  CustomerReference: 'Orders', DeliveryAddress: 'Orders',
  LineNo: 'OrderLine', SerialNo: 'OrderLine', Quantity: 'OrderLine',
  // Batch/serie
  BatchNo: 'OrderBatchNo', ExpiryDate: 'OrderBatchNo', BestBeforeDate: 'OrderBatchNo',
  // SSCC/pall
  SSCC: 'OrderSSCC', GTIN: 'OrderSSCC', PalletNo: 'PListSSCC',
};

const DB: EnrichmentKind = 'db';
const OVERRIDE: EnrichmentKind = 'override-only';
const DOCUMENT: EnrichmentKind = 'document';
const LEGACY: EnrichmentKind = 'legacy';

/** Verifisert mapping per rapporttype-verdi (jf. Reports.ReportType). */
export const REPORT_TYPE_ENRICHMENT: Record<number, EnrichmentInfo> = {
  1:  { kind: DB, dbSources: ['Items'], note: 'Vareetikett — kun vare-master.' },
  2:  { kind: DB, dbSources: ['Items', 'POrders', 'POrderLine'], note: 'Vare + innkjøpsordre (linje).' },
  3:  { kind: DB, dbSources: ['Items', 'Orders', 'OrderLine'], note: 'Vare + salgsordre. Kun ordrens FØRSTE linje; når ikke batch/SSCC.' },
  4:  { kind: DB, dbSources: ['StcBin', 'StcZone', 'Warehouse'], note: 'Lokasjon. Krever tallbasert WarehouseId; ellers kun override.' },
  5:  { kind: LEGACY, dbSources: [], note: 'Legacy — ingen aktiv utskriftsflyt produserer type 5 i dag.' },
  10: { kind: DOCUMENT, dbSources: [], note: 'Pakkseddel (dokument). LayoutJson brukes IKKE ved utskrift i dag — data kommer fra rapportgeneratoren.' },
  11: { kind: DOCUMENT, dbSources: [], note: 'Pakkseddel detaljert / telleliste (dokument). LayoutJson brukes ikke ved utskrift i dag.' },
  40: { kind: DB, dbSources: ['Orders', 'OrderLine', 'OrderBatchNo', 'OrderSSCC', 'Items'], note: 'Kolli. Ordre + (hvis plukk-koblet linje) batch/SSCC/vare.' },
  41: { kind: OVERRIDE, dbSources: [], note: 'Batch-pakkeetikett — tom kontekst. Kun PackageNo/VisiblePackageNo via override + firma/system.' },
  50: { kind: DB, dbSources: ['OrderSSCC', 'Orders', 'OrderLine', 'OrderBatchNo', 'Items', 'PListSSCC'], note: 'SSCC/pall — bredest. SSCC kjeder gjennom ordre/linje/batch/vare.' },
  51: { kind: DB, dbSources: ['OrderSSCC', 'Orders', 'OrderLine', 'OrderBatchNo', 'Items', 'PListSSCC'], note: 'SSCC transport — som type 50.' },
  70: { kind: OVERRIDE, dbSources: [], note: 'Sorteringskasse — tom kontekst. Kun StcBoxNo/StcBoxTypeID via override + firma/system.' },
  20: { kind: LEGACY, dbSources: [], note: 'Legacy — fraktetiketter går via TA og logges som 99, ikke 20.' },
  30: { kind: LEGACY, dbSources: [], note: 'Legacy — ingen aktiv produserende flyt.' },
  99: { kind: OVERRIDE, dbSources: [], note: 'Ekstern/TA — ikke designer-beriket på vanlig måte.' },
};

/**
 * Slår opp berikelsesinfo for en rapporttype, med håndtering av 60–69 (generisk,
 * uten enum-verdi) og ukjente verdier.
 */
export function getEnrichmentInfo(reportType: number | undefined): EnrichmentInfo {
  if (reportType === undefined || reportType === null) {
    return { kind: OVERRIDE, dbSources: [], note: 'Ukjent rapporttype.' };
  }
  if (REPORT_TYPE_ENRICHMENT[reportType]) {
    return REPORT_TYPE_ENRICHMENT[reportType];
  }
  if (reportType >= 60 && reportType <= 69) {
    return { kind: OVERRIDE, dbSources: [], note: 'Generisk etikett (ingen enum-verdi) — tom kontekst. Kun FreeText via override + firma/system.' };
  }
  return { kind: OVERRIDE, dbSources: [], note: `Type ${reportType} — ingen kjent DB-berikelse; kun override + firma/system.` };
}

export type PlaceholderFill = 'db' | 'system' | 'override';

/**
 * Avgjør om en plassholder-nøkkel fylles automatisk for en gitt rapporttype:
 *  - 'system'   → firma/systemverdi, fylles alltid
 *  - 'db'       → fylles fra database (kilde-tabell er i typens dbSources)
 *  - 'override' → fylles KUN hvis print-kallet sender verdien eksplisitt
 *
 * Håndterer både enkle nøkler ("ItemNo") og tabell-prefiksede ("Items.Description1").
 */
export function classifyPlaceholderKey(key: string, reportType: number | undefined): PlaceholderFill {
  if (SYSTEM_KEYS.has(key)) return 'system';

  const info = getEnrichmentInfo(reportType);
  const source = key.includes('.') ? key.split('.')[0] : KEY_SOURCE_TABLE[key];

  // SystemSetup-prefiksede tabellkolonner er alltid tilgjengelige
  if (source === 'SystemSetup') return 'system';

  if (source && info.dbSources.includes(source)) return 'db';
  return 'override';
}

/** Symbol brukt som prefiks i plassholder-nedtrekk. */
export const FILL_MARKER: Record<PlaceholderFill, string> = {
  db: '●',
  system: '●',
  override: '○',
};
